import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Text, useTheme } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { useTranslation } from 'react-i18next';

import {
  apiFetch,
  advanceOnboardingState,
  createCheckoutSession,
  createSubscription,
  createTrialSubscriptionNoCard,
  getOnboardingState,
} from '../../../src/api/apiService';
import { API_BASE, API_ENDPOINTS } from '../../../src/api/config';
import { HandlePayment } from '../../../src/components/HandlePayment';
import { useUser } from '@/src/hooks/useUser';
import { useGpsContractGate } from '@/src/hooks/useGpsContractGate';
import { usePaywallConfig } from '@/src/hooks/usePaywallConfig';
import type { PaywallFeature } from '@/src/types/paywall';
import {
  isAuthoritativeOnboardingState,
  isOnboardingSelection,
  routeForOnboardingState,
} from '@/src/utils/onboardingResume';
import {
  billingCycleForPlan,
  buildPaidCheckoutPayload,
  buildPricingBreakdown,
  checkoutSelectionForCatalog,
  featureForPlan,
  getTrialDays,
  type BillingCycle,
  type CatalogPlan,
} from '@/src/features/onboarding/ownerPlanSelection';
import { toIntlLocale } from '@/src/lib/intlLocale';
import { resolveEnvironment, track } from '@/src/lib/analytics';
import { GpsContractDeclinedNotice } from '@/src/features/onboarding/gpsDecline/GpsContractDeclinedNotice';

const FEATURES: PaywallFeature[] = ['hours', 'gps', 'combo'];
const BILLING_CYCLES: BillingCycle[] = ['monthly', 'annual'];

type RouteValue = string | string[] | undefined;

const firstRouteValue = (value: RouteValue): string | undefined => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return undefined;
};

const isFeature = (value: string | undefined): value is PaywallFeature =>
  !!value && FEATURES.includes(value as PaywallFeature);

const isBillingCycle = (value: string | undefined): value is BillingCycle =>
  !!value && BILLING_CYCLES.includes(value as BillingCycle);

const isConfirmedNativeCheckoutSuccess = (result: { type: string; url?: string }): boolean => {
  if (result.type !== 'success' || !result.url) return false;
  try {
    return new URL(result.url).searchParams.get('purchase_success') === 'true';
  } catch {
    return false;
  }
};

export default function OwnerOnboardingWrapper() {
  return <OwnerOnboarding />;
}

function OwnerOnboarding() {
  const { t, i18n } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{
    licenses?: RouteValue;
    feature?: RouteValue;
    billing?: RouteValue;
    price_id?: RouteValue;
  }>();
  const { userData, fetchUserData } = useUser();
  const ownerPersona = userData?.company?.is_individual ? 'OWN-IND' : 'OWN-ENT';
  const isOwner = !!userData?.groups?.includes('Owner');
  const userLoaded = Array.isArray(userData?.groups);
  const accountKey = String(userData?.id ?? userData?.email ?? '');

  const licensesValue = firstRouteValue(params.licenses);
  const featureValue = firstRouteValue(params.feature);
  const billingValue = firstRouteValue(params.billing);
  const priceId = firstRouteValue(params.price_id);
  const quantity = Number(licensesValue);
  const feature = isFeature(featureValue) ? featureValue : null;
  const billing = isBillingCycle(billingValue) ? billingValue : null;
  const hasSelectionParams = [licensesValue, featureValue, billingValue, priceId].some(
    value => value !== undefined,
  );
  const quantityValid = Number.isInteger(quantity) && quantity >= 1;
  const selectionShapeValid = quantityValid && !!feature && !!billing && !!priceId;

  const { config, isLoading: configLoading, error: configError } = usePaywallConfig(
    'owner',
    feature ?? 'hours',
  );

  const [resolvedPlan, setResolvedPlan] = useState<CatalogPlan | null>(null);

  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  // ALL-134: the owner declined the GPS contract for this selection. Nothing was
  // charged; the screen offers Hours only, the contract again, or the chat.
  const [declined, setDeclined] = useState<{ feature: PaywallFeature; billing: BillingCycle } | null>(
    null,
  );
  const [switchingToHours, setSwitchingToHours] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  // Server-confirmed authorization is bound to the account AND the selection.
  const [authorization, setAuthorization] = useState<{ account: string; selection: string } | null>(
    null,
  );
  const [recheckCount, setRecheckCount] = useState(0);
  const resolvedSelectionKey = useRef<string | null>(null);
  const accountRef = useRef('');
  accountRef.current = accountKey;
  const submitKey = useRef<string | null>(null);
  const {
    requestContract: requestGpsContract,
    contractModal: gpsContractModal,
    loadErrorMessage: gpsContractLoadError,
  } = useGpsContractGate();

  useEffect(() => {
    // A disposed parent never renders the next account. Invalidate retained
    // callbacks on cleanup; restore the identity on setup (including StrictMode).
    accountRef.current = accountKey;
    return () => { accountRef.current = ''; };
  }, [accountKey]);

  const trialDays = useMemo(() => {
    if (!resolvedPlan || !feature || !billing) return null;
    return getTrialDays(resolvedPlan, feature, billing, config);
  }, [billing, config, feature, resolvedPlan]);

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat(toIntlLocale(i18n.language), {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);

  const resolveSelection = useCallback(async () => {
    if (!selectionShapeValid || !feature || !billing || !priceId) {
      setResolvedPlan(null);
      setResolveError(t(
        'checkoutSelectionInvalid',
        ));
      return;
    }
    if (feature === 'hours' && billing === 'monthly' && configLoading) return;
    if (feature === 'hours' && billing === 'monthly' && (configError || !config)) {
      setResolvedPlan(null);
      setResolveError(t(
        'eligibilityUnavailable',
        ));
      return;
    }

    setResolving(true);
    setResolveError(null);
    try {
      const response = await apiFetch<{ prices: CatalogPlan[] }>(
        `${API_ENDPOINTS.SUBSCRIPTIONS}prices/?feature=${encodeURIComponent(feature)}`,
      );
      const prices = response.ok && Array.isArray(response.data?.prices)
        ? response.data.prices
        : [];
      const exactPlan = checkoutSelectionForCatalog(prices, {
        feature,
        billing,
        priceId,
      });
      if (!exactPlan) throw new Error('checkout_selection_not_in_catalog');

      const monthlyCandidates = prices.filter(
        plan =>
          billingCycleForPlan(plan) === 'monthly' &&
          (plan.product?.metadata?.feature ?? plan.metadata?.feature) === feature,
      );
      const monthlyPlan = monthlyCandidates.length === 1 ? monthlyCandidates[0] : null;
      buildPricingBreakdown({
        plan: exactPlan,
        monthlyPlan,
        feature,
        billing,
        quantity,
        trialDays: getTrialDays(exactPlan, feature, billing, config),
      });
      setResolvedPlan(exactPlan);
    } catch {
      setResolvedPlan(null);
      setResolveError(t(
        'checkoutSelectionInvalid',
        ));
    } finally {
      setResolving(false);
    }
    // The catalog identity owns this request; translation function identity must
    // not refetch the selected plan on every render or language update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing, config, configError, configLoading, feature, priceId, quantity, selectionShapeValid]);

  const selectionKey = `${licensesValue ?? ''}:${featureValue ?? ''}:${billingValue ?? ''}:${priceId ?? ''}`;

  useEffect(() => {
    if (feature === 'hours' && billing === 'monthly' && configLoading) return;
    if (resolvedSelectionKey.current === selectionKey) return;
    resolvedSelectionKey.current = selectionKey;
    void resolveSelection();
  }, [billing, configLoading, feature, resolveSelection, selectionKey]);

  const markOnboardingDone = useCallback(async (progressStep = 'tax_sync') => {
    // This callback can be retained by the payment child or a provider event and
    // run after an account switch/unmount. Capture the account it was created for
    // and fence before every mutation and after every await; a request already
    // started for that account is not undone, but nothing continues under another.
    const startedFor = accountKey;
    const stale = () => !startedFor || accountRef.current !== startedFor;
    if (stale()) return;
    try {
      if (typeof advanceOnboardingState === 'function') {
        await advanceOnboardingState(ownerPersona, progressStep);
      }
    } catch {
      // The subscription/webhook remains the authority if pointer recording fails.
    }
    if (stale()) return;
    try {
      await AsyncStorage.setItem('ownerOnboardingDone', 'true');
    } catch {
      // Local completion persistence is best effort after the remote result is authoritative.
    }
    if (stale()) return;
    try {
      await fetchUserData();
    } catch {
      // The subscription result remains authoritative; refresh is best effort.
    }
    if (stale()) return;
    router.replace('/dashboard/home');
  }, [accountKey, fetchUserData, ownerPersona, router]);

  const resetPurchaseAttempt = useCallback((message: string | null = null) => {
    submitKey.current = null;
    setPaymentVisible(false);
    setClientSecret(null);
    setSubscriptionId(null);
    setSubmitting(false);
    setPurchaseError(message);
  }, []);

  const clearDecline = useCallback(() => {
    setDeclined(null);
    setSwitchError(null);
  }, []);

  // A different account must never inherit another account's in-flight or failed attempt.
  const previousAccount = useRef(accountKey);
  useEffect(() => {
    if (previousAccount.current === accountKey) return;
    previousAccount.current = accountKey;
    setAuthorization(null);
    resetPurchaseAttempt();
    clearDecline();
  }, [accountKey, clearDecline, resetPurchaseAttempt]);

  // A decline belongs to the selection it was made for.
  const declinedSelectionKey = useRef<string | null>(null);
  useEffect(() => {
    if (declinedSelectionKey.current !== null && declinedSelectionKey.current !== selectionKey) {
      declinedSelectionKey.current = null;
      clearDecline();
    }
  }, [clearDecline, selectionKey]);

  const retryPurchase = useCallback(() => {
    // Retry is a new decision: drop the old authorization and re-read own server state.
    setAuthorization(null);
    resetPurchaseAttempt();
    setRecheckCount(count => count + 1);
  }, [resetPurchaseAttempt]);

  const handlePaymentDismiss = useCallback(() => {
    resetPurchaseAttempt(t(
      'purchaseCancelled',
      ));
  }, [resetPurchaseAttempt, t]);

  const failPurchase = useCallback((error: unknown) => {
    submitKey.current = null;
    setPurchaseError(
      error instanceof Error
        ? error.message
        : t('purchaseFailed'),
    );
    setSubmitting(false);
  }, [t]);

  const startPaidCheckout = useCallback(async (
    payload: ReturnType<typeof buildPaidCheckoutPayload>,
    stale: () => boolean,
  ) => {
    if (Platform.OS === 'web') {
      const response = await createSubscription(payload);
      if (stale()) return;
      if (!response.ok || !response.data?.client_secret) {
        throw new Error(t('subscriptionError'));
      }
      setClientSecret(response.data.client_secret);
      setSubscriptionId(response.data.subscription_id);
      setPaymentVisible(true);
      return;
    }

    const frontendReturnUrl = Linking.createURL('/dashboard/home');
    const stripeReturnUrl = `${API_BASE}/api/subscriptions/stripe-redirect/?mobile=true`;
    const appDeepLink = Linking.createURL('/dashboard/home');
    const response = await createCheckoutSession({
      ...payload,
      return_url: stripeReturnUrl,
      frontend_return_url: frontendReturnUrl,
      is_mobile: true,
    });
    if (stale()) return;
    if (!response.ok || !response.data?.checkout_url) {
      throw new Error(t('subscriptionError'));
    }
    const result = await WebBrowser.openAuthSessionAsync(response.data.checkout_url, appDeepLink);
    if (stale()) return;
    if (isConfirmedNativeCheckoutSuccess(result)) {
      await markOnboardingDone();
    } else {
      throw new Error(t(
        'purchaseCancelled',
        ));
    }
  }, [markOnboardingDone, t]);

  const submitSelection = useCallback(async () => {
    if (!resolvedPlan || !feature || !billing || !quantityValid || submitting) return;
    const key = `${feature}:${billing}:${resolvedPlan.price_id}:${quantity}`;
    if (submitKey.current === key) return;
    submitKey.current = key;
    const startedFor = accountRef.current;
    const stale = () => accountRef.current !== startedFor;
    setSubmitting(true);
    setPurchaseError(null);

    try {
      if (trialDays != null) {
        if (feature !== 'hours' || billing !== 'monthly') {
          throw new Error('trial_not_allowed_for_selection');
        }
        const response = await createTrialSubscriptionNoCard({
          price_id: resolvedPlan.price_id,
          quantity,
        });
        if (stale()) return;
        if (!response.ok || !response.data?.subscription_id) {
          throw new Error(t('subscriptionError'));
        }
        await markOnboardingDone('trial_active_no_card');
        return;
      }

      const payload = buildPaidCheckoutPayload(resolvedPlan, feature, quantity);
      if (feature === 'gps' || feature === 'combo') {
        // D-059 / ALL-125: a GPS/Combo purchase starts only after the contract for
        // this exact price and quantity was accepted (same step as Gerir Licenças).
        const outcome = await requestGpsContract(resolvedPlan, quantity, {
          onAccepted: async () => {
            if (stale()) return;
            try {
              await startPaidCheckout(payload, stale);
            } catch (error) {
              if (!stale()) failPurchase(error);
            }
          },
          onCancelled: reason => {
            if (stale()) return;
            if (reason !== 'rejected') {
              resetPurchaseAttempt(t('purchaseFailed'));
              return;
            }
            resetPurchaseAttempt();
            declinedSelectionKey.current = selectionKey;
            setSwitchError(null);
            setDeclined({ feature, billing });
            track('gps_contract_declined', {
              screen: 'onboarding',
              feature,
              billing,
              licenses: quantity,
              user_role: 'owner',
              app_variant: resolveEnvironment(),
            });
          },
        });
        if (stale()) return;
        if (outcome === 'load_error') throw new Error(gpsContractLoadError);
        if (outcome === 'shown') return;
      }
      await startPaidCheckout(payload, stale);
    } catch (error) {
      if (stale()) return;
      failPurchase(error);
    }
  }, [
    billing,
    failPurchase,
    feature,
    gpsContractLoadError,
    markOnboardingDone,
    quantity,
    quantityValid,
    requestGpsContract,
    resetPurchaseAttempt,
    resolvedPlan,
    selectionKey,
    startPaidCheckout,
    submitting,
    t,
    trialDays,
  ]);

  useEffect(() => {
    // URL params are only a hint. A purchase may start only after the server
    // confirmed, for this account, an incomplete config_phase2 with this exact
    // selection (D-047); stale params of a completed owner never reach a provider.
    if (
      resolvedPlan
      && !!accountKey
      && authorization?.account === accountKey
      && authorization.selection === selectionKey
      && !submitting
      && !paymentVisible
      && !purchaseError
      && !declined
    ) {
      void submitSelection();
    }
  }, [
    accountKey,
    authorization,
    declined,
    paymentVisible,
    purchaseError,
    resolvedPlan,
    selectionKey,
    submitSelection,
    submitting,
  ]);

  useEffect(() => {
    let cancelled = false;
    const resumeExistingOwner = async () => {
      if (!userLoaded) return;
      // Fail closed through the canonical error route: no identity or no Owner
      // role never reaches state reads or provider calls, and never spins forever.
      if (!isOwner || !accountKey) {
        if (hasSelectionParams) router.replace('/onboarding/state-error');
        return;
      }
      try {
        const response = await getOnboardingState(hasSelectionParams ? { fresh: true } : {});
        // A late response belongs to the account/params that issued it.
        if (cancelled) return;
        if (!response.ok || !isAuthoritativeOnboardingState(response.data)) {
          router.replace('/onboarding/state-error');
          return;
        }
        if (
          response.data.current_step === 'config_phase2'
          && isOnboardingSelection(response.data.selection)
        ) {
          const selection = response.data.selection;
          if (
            hasSelectionParams
            && String(selection.quantity) === licensesValue
            && selection.feature === featureValue
            && selection.billing === billingValue
            && selection.price_id === priceId
          ) {
            setAuthorization({ account: accountKey, selection: selectionKey });
            return;
          }
          router.replace({
            pathname: '/onboarding/owner',
            params: {
              licenses: String(selection.quantity),
              feature: selection.feature,
              billing: selection.billing,
              price_id: selection.price_id,
            },
          });
          return;
        }
        const route = routeForOnboardingState(response.data);
        // The owner chat is the single intermediate surface. Enter it via its
        // dedicated child route instead of replacing /onboarding/owner with
        // itself (which caused a navigation loop). All later terminal routes
        // are taken verbatim from the server state.
        router.replace(route === '/onboarding/owner' ? '/onboarding/owner/fleet-size' : route);
      } catch {
        if (!cancelled) router.replace('/onboarding/state-error');
      }
    };
    void resumeExistingOwner();
    return () => {
      cancelled = true;
    };
    // accountKey (not the userData object) scopes the request: a profile refresh
    // after a successful purchase must not re-run the resume decision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountKey,
    billingValue,
    featureValue,
    hasSelectionParams,
    isOwner,
    licensesValue,
    priceId,
    recheckCount,
    userLoaded,
  ]);

  const returnToChat = () => router.replace('/onboarding/owner/fleet-size');

  // Clearing the decline lets the authorized selection submit again, which
  // reopens the contract for this same price and quantity.
  const reviewContract = () => clearDecline();

  const continueWithHoursOnly = async () => {
    if (!declined || switchingToHours || !quantityValid) return;
    const declinedFrom = declined;
    const startedFor = accountRef.current;
    const stale = () => accountRef.current !== startedFor;
    setSwitchingToHours(true);
    setSwitchError(null);
    try {
      const response = await apiFetch<{ prices: CatalogPlan[] }>(
        `${API_ENDPOINTS.SUBSCRIPTIONS}prices/?feature=hours`,
      );
      if (stale()) return;
      const prices = response.ok && Array.isArray(response.data?.prices) ? response.data.prices : [];
      const hoursPlans = prices.filter(
        plan => featureForPlan(plan) === 'hours' && billingCycleForPlan(plan) === declinedFrom.billing,
      );
      if (hoursPlans.length !== 1) throw new Error('hours_plan_unavailable');
      const hoursPlan = hoursPlans[0];
      const recorded = await advanceOnboardingState(ownerPersona, 'config_phase2', {
        selection: {
          feature: 'hours',
          billing: declinedFrom.billing,
          price_id: hoursPlan.price_id,
          quantity,
        },
      });
      if (stale()) return;
      if (!recorded.ok) throw new Error('hours_selection_not_recorded');
      track('gps_contract_declined_to_hours', {
        screen: 'onboarding',
        feature: declinedFrom.feature,
        billing: declinedFrom.billing,
        licenses: quantity,
        user_role: 'owner',
        app_variant: resolveEnvironment(),
      });
      router.replace({
        pathname: '/onboarding/owner',
        params: {
          licenses: String(quantity),
          feature: 'hours',
          billing: declinedFrom.billing,
          price_id: hoursPlan.price_id,
        },
      });
    } catch {
      if (!stale()) setSwitchError(t('gpsContractDeclinedSwitchError'));
    } finally {
      if (!stale()) setSwitchingToHours(false);
    }
  };

  if (!hasSelectionParams) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator accessibilityLabel={t('loading')} />
      </View>
    );
  }

  if (resolving || (feature === 'hours' && billing === 'monthly' && configLoading)) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator accessibilityLabel={t('loading')} />
      </View>
    );
  }

  if (resolveError || !resolvedPlan) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <Text
          testID="checkout-selection-invalid"
          accessibilityRole="alert"
          style={{ color: theme.colors.error, textAlign: 'center' }}
        >
          {resolveError || t('checkoutSelectionInvalid')}
        </Text>
        <Button mode="outlined" onPress={returnToChat} testID="return-to-chat">
          {t('returnToChat')}
        </Button>
      </View>
    );
  }

  const isTrial = trialDays != null;
  const recurringTotal = resolvedPlan.unit_amount * quantity;
  const setupTotal = resolvedPlan.setup_fee_unit_amount
    ? resolvedPlan.setup_fee_unit_amount * quantity
    : 0;

  return (
    <ScrollView contentContainerStyle={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.summary} testID="checkout-selection-summary">
        <Text variant="headlineSmall" style={{ color: theme.colors.primary }}>
          {resolvedPlan.product.name}
        </Text>
        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
          {billing === 'monthly' ? t('planMonthlyTitle') : t('planAnnualTitle')}
          {' · '}
          {quantity} {t('licensesShort')}
        </Text>
        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
          {formatCurrency(recurringTotal, resolvedPlan.currency)} / {billing === 'monthly'
            ? t('periodMonth')
            : t('periodYear')}
        </Text>
        {setupTotal > 0 && (
          <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
            {t('reviewSetupTotal', {
              total: formatCurrency(setupTotal, resolvedPlan.setup_fee_currency || resolvedPlan.currency),
            })}
          </Text>
        )}
        <Text variant="labelLarge" style={{ color: theme.colors.primary }}>
          {t('vatIncluded')}
        </Text>
        {purchaseError && (
          <>
            <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>
              {purchaseError}
            </Text>
            <Button mode="contained" onPress={retryPurchase} testID="purchase-retry">
              {t('retryPayment')}
            </Button>
            <Button mode="outlined" onPress={returnToChat} testID="purchase-back">
              {t('returnToChat')}
            </Button>
          </>
        )}
        {declined && (
          <GpsContractDeclinedNotice
            onContinueHoursOnly={() => void continueWithHoursOnly()}
            onReviewContract={reviewContract}
            onReturnToChat={returnToChat}
            busy={switchingToHours}
            errorMessage={switchError}
          />
        )}
        {submitting && <ActivityIndicator accessibilityLabel={t('processing')} />}
      </View>

      {clientSecret && (
        <HandlePayment
          visible={paymentVisible}
          onDismiss={handlePaymentDismiss}
          clientSecret={clientSecret}
          priceId={resolvedPlan.price_id}
          quantity={quantity}
          setupPriceId={resolvedPlan.setup_fee_price_id}
          deviceQuantity={feature === 'gps' || feature === 'combo' ? quantity : undefined}
          isTrial={isTrial}
          subscriptionId={subscriptionId || undefined}
          onSuccess={markOnboardingDone}
        />
      )}
      {gpsContractModal}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 24,
  },
  container: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  summary: {
    width: '100%',
    maxWidth: 480,
    alignItems: 'center',
    gap: 8,
  },
});
